import type { ReactNode } from "react";
import { useCallback, useId } from "react";

import { useTranslations } from "@ui/providers/useTranslations";
import classNames from "classnames";

import type { TextInputProps as CoreTextInputProps } from "@jti/core-components";
import { TextInput as CoreTextInput } from "@jti/core-components";

import styles from "./TextInput.module.scss";

export type TextInputProps = {
  formatter?: (input: string) => string;
  label?: string;
  labelId?: string;
  successText?: ReactNode;
  errorText?: ReactNode;
  helperText?: ReactNode;
  isError?: boolean;
  isSuccess?: boolean;
  isOptionalLabelVisible?: boolean;
  necessityIndicator?: boolean;
  containerClassName?: string;
  value?: string | number | undefined;
} & Omit<CoreTextInputProps, "value">;

const getStatus = (isError?: boolean, isSuccess?: boolean): CoreTextInputProps["status"] => {
  if (isError) return "error";
  if (isSuccess) return "success";
  return "default";
};

// DS3 renders the asterisk for every required field, so the necessity-indicator variant is used
// for optional fields. Consumers that don't want an asterisk at all (e.g. Contact Us form, per
// design) can opt into the necessity-indicator variant themselves via necessityIndicator.
const getLabelFields = (
  label: string | undefined,
  necessityIndicator: boolean | undefined,
  required: boolean | undefined
): CoreTextInputProps["labelFields"] => {
  if (!label) return undefined;
  if (necessityIndicator) return { label, necessityIndicator: true };
  if (required) return { label, asterisk: true };

  return { label, necessityIndicator: true };
};

const getDisplayedHelperText = ({
  status,
  successText,
  errorText,
  helperText,
}: {
  status: TextInputProps["status"];
  successText: TextInputProps["successText"];
  errorText: TextInputProps["errorText"];
  helperText: TextInputProps["helperText"];
}) => {
  let supportingText = helperText;
  if (status === "success" && successText) {
    supportingText = successText;
  } else if (status === "error" && errorText) {
    supportingText = errorText;
  }

  return supportingText;
};

export const TextInput = ({
  isOptionalLabelVisible,
  necessityIndicator,
  id,
  formatter,
  onChange = () => void 0,
  label,
  helperText,
  isError,
  errorText,
  isSuccess,
  successText,
  containerClassName,
  labelId,
  elemBefore,
  elemAfter,
  type,
  value,
  required,
  ref,
  ...rest
}: TextInputProps) => {
  const { name } = rest;
  const { __ } = useTranslations();
  const fallbackId = useId();
  const inputId = id || name || fallbackId;

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const _value = formatter ? (formatter(e.target.value) ?? "") : (e.target.value ?? "");

      onChange({
        ...e,
        target: {
          ...e.target,
          value: _value,
        },
      });

      if (formatter) {
        const caretStart = e.target.selectionStart;
        const caretEnd = e.target.selectionEnd;
        const end =
          (caretEnd ?? 0) + (_value.length !== e.target.value.length ? _value.length - e.target.value.length : 0);
        const start = caretStart === caretEnd ? end : caretStart;
        setTimeout(() => {
          e.target.setSelectionRange(start, end);
        }, 0);
      }
    },
    [onChange, formatter]
  );

  // TODO: isOptionalLabelVisible (label-concatenation) and necessityIndicator (FormFieldLabel's
  // indicator span) are two separate mechanisms for showing "(optional)"/"(required)" next to the
  // label. Consolidate on necessityIndicator and drop isOptionalLabelVisible in a follow-up.
  const optionalLabelText = label && !required && isOptionalLabelVisible ? __("form_fields.optional") : "";
  const displayedLabel = optionalLabelText ? `${label} ${optionalLabelText}` : label;

  const labelFields = getLabelFields(displayedLabel, necessityIndicator, required);

  const status = getStatus(isError, isSuccess);
  const displayedHelperText = getDisplayedHelperText({
    status,
    successText,
    errorText,
    helperText,
  });

  return (
    <div className={classNames(styles.container, containerClassName)}>
      <CoreTextInput
        {...rest}
        ref={ref}
        required={required}
        id={inputId}
        aria-labelledby={labelId}
        type={type}
        value={value}
        labelFields={labelFields}
        labelId={labelId}
        status={status}
        helperText={displayedHelperText}
        elemBefore={elemBefore}
        elemAfter={elemAfter}
        onChange={handleChange}
      />
    </div>
  );
};
